import { BaseQueryFn, EndpointBuilder } from "@reduxjs/toolkit/query";
import { Identifier } from "@/types/domain";
import { Content } from "@/types/squidex";
import { domainTools } from "@lib/domain";
import { httpTools } from "@lib/redux/http-utils";
import { apiSlice } from "@features/application/api-slice";
import { createBulkReadMutation } from "@features/feed/bulk-read";
import { CacheTagType, NestedEntitiesProvidesId } from "@features/application/types";
import {
  ActivityContentDifferenceEntity,
  MemberBiographyContentDifferenceEntity,
  ResourceContentDifferenceOutputView,
  toResourceContentDifferenceEntity,
} from "@features/events-program/types/publication-diff";
import { proposalsTools } from "@features/proposals/proposals-tools";
import {
  ActivityContentData,
  ActivityEntity,
  ActivityInputView,
  ActivityOutputView,
  SpeakerRoomCreationInputView,
} from "@features/proposals/types/activity";
import {
  ActivityMemberEntity,
  ActivityMemberInputView,
  ActivityMemberOutputView,
  ActivityMemberRegistrationInputView,
  ContributorBiographyContentData,
  ContributorBiographyEntity,
  ContributorBiographyOutputView,
} from "@features/proposals/types/member";
import {
  ProposalEntity,
  ProposalInputView,
  ProposalOutputView,
  SpeakerAreaProposalEntity,
  SpeakerAreaProposalOutputView,
} from "@features/proposals/types/proposal";
import {
  ContributorProposalEntity,
  ContributorProposalOutputView,
} from "@features/proposals/types/contributor-proposal";
import routes from "@features/routes";

const convertToDomainEntity = (data?: SpeakerAreaProposalOutputView) =>
  data ? proposalsTools.convertSpeakerAreaProposalToDomainEntity(data) : undefined;

const convertActivityToDomainEntity = (data?: ActivityOutputView) =>
  data ? proposalsTools.activityTools.convertToDomainEntity(data) : undefined;

const getActivityMember = (
  builder: EndpointBuilder<BaseQueryFn, string, "api">,
  type?: CacheTagType | null,
) =>
  httpTools.getOnceResponse<ActivityMemberOutputView, ActivityMemberEntity>(
    builder,
    routes.proposals.api.activity_member,
    type,
    (data) => (data ? proposalsTools.activityMemberTools.convertToDomainEntity(data) : undefined),
  );

const contributorProposalsExtraEntitiesCacheExtractor: NestedEntitiesProvidesId<ContributorProposalEntity>[] =
  [
    {
      type: "Activity",
      extractor: (proposal) => proposal?.activity?.id,
    },
    {
      type: "ActivityMember",
      extractor: (proposal) => proposal?.member?.id,
    },
  ];

export const proposalsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSpeakersProposals: httpTools.getMultipleResponse<
      SpeakerAreaProposalOutputView,
      SpeakerAreaProposalEntity
    >(
      builder,
      routes.proposals.api.speaker_area_proposals,
      "SpeakerAreaProposal",
      (data) =>
        data
          ?.map(proposalsTools.convertSpeakerAreaProposalToDomainEntity)
          .sort(domainTools.sortByIdDesc),
      [
        {
          type: "Activity",
          extractor: (proposal) => proposal?.activity?.id,
        },
        {
          type: "ActivityMember",
          extractor: (proposal) => proposal?.member?.id,
        },
      ],
    ),
    getSpeakerProposal: httpTools.getOnceResponse<
      SpeakerAreaProposalOutputView,
      SpeakerAreaProposalEntity
    >(
      builder,
      routes.proposals.api.speaker_area_proposal,
      "SpeakerAreaProposal",
      convertToDomainEntity,
      [
        {
          type: "Activity",
          extractor: (proposal) => proposal?.activity?.id,
        },
        {
          type: "ActivityMember",
          extractor: (proposal) => proposal?.member?.id,
        },
      ],
    ),
    // GET /api/v2/contributors/{contributorId}/proposals
    getContributorProposals: httpTools.getMultipleResponse<
      ContributorProposalOutputView,
      ContributorProposalEntity
    >(
      builder,
      routes.proposals.api.contributor_proposals,
      "ContributorProposal",
      (data) => data?.map(proposalsTools.convertContributorProposalToDomainEntity),
      contributorProposalsExtraEntitiesCacheExtractor,
    ),
    getProposal: httpTools.getOnceResponse<ProposalOutputView, ProposalEntity>(
      builder,
      routes.proposals.api.proposal,
      "Proposal",
      (data) => (data ? proposalsTools.convertProposalToDomainEntity(data) : undefined),
    ),
    patchProposal: httpTools.patchOnceResponse<
      ProposalOutputView,
      ProposalEntity,
      ProposalInputView
    >(
      builder,
      routes.proposals.api.proposal,
      "Proposal",
      (data) => (data ? proposalsTools.convertProposalToDomainEntity(data) : undefined),
      [
        {
          type: "ProgramProposal",
          extractor: (data) => data?.id,
        },
      ],
    ),
    getActivity: httpTools.getOnceResponse<ActivityOutputView, ActivityEntity>(
      builder,
      routes.proposals.api.activity,
      "Activity",
      convertActivityToDomainEntity,
    ),
    bulkReadActivities: createBulkReadMutation<ActivityOutputView>(
      builder,
      routes.proposals.api.activities_bulk_read,
    ),
    patchActivity: httpTools.patchOnceResponse<
      ActivityOutputView,
      ActivityEntity,
      ActivityInputView
    >(builder, routes.proposals.api.activity, "Activity", convertActivityToDomainEntity),
    postActivityPresentation: httpTools.postOnceResponse<
      ActivityOutputView,
      ActivityEntity,
      FormData
    >(
      builder,
      routes.proposals.api.activity_upload_presentation,
      "Activity",
      convertActivityToDomainEntity,
      "once",
    ),
    getActivityMembers: httpTools.getMultipleResponse<
      ActivityMemberOutputView,
      ActivityMemberEntity
    >(builder, routes.proposals.api.activity_members, "ActivityMember", (data) =>
      data?.map(proposalsTools.activityMemberTools.convertToDomainEntity),
    ),
    bulkReadActivityMembers: createBulkReadMutation<ActivityMemberOutputView>(
      builder,
      routes.proposals.api.activity_members_bulk_read,
    ),
    getActivityMember: getActivityMember(builder, "ActivityMember"),
    getActivityMemberNoCache: getActivityMember(builder, null),
    postActivityMember: httpTools.postOnceResponse<
      ActivityMemberOutputView,
      ActivityMemberEntity,
      ActivityMemberRegistrationInputView
    >(builder, routes.proposals.api.activity_members, "ActivityMember", (data) =>
      data ? proposalsTools.activityMemberTools.convertToDomainEntity(data) : undefined,
    ),
    postActivityMemberPhoto: httpTools.postOnceResponse<
      ActivityMemberOutputView,
      ActivityMemberEntity,
      FormData
    >(
      builder,
      routes.proposals.api.activity_member_upload_photo,
      "ActivityMember",
      (data) => (data ? proposalsTools.activityMemberTools.convertToDomainEntity(data) : undefined),
      "once",
    ),
    patchActivityMember: httpTools.patchOnceResponse<
      ActivityMemberOutputView,
      ActivityMemberEntity,
      ActivityMemberInputView
    >(builder, routes.proposals.api.activity_member, "ActivityMember", (data) =>
      data ? proposalsTools.activityMemberTools.convertToDomainEntity(data) : undefined,
    ),
    deleteActivityMember: httpTools.deleteOnceResponse(
      builder,
      routes.proposals.api.activity_member,
      "ActivityMember",
    ),
    getActivityMemberBiographiesNoCache: httpTools.getMultipleResponse<
      ContributorBiographyOutputView,
      ContributorBiographyEntity
    >(builder, routes.proposals.api.activity_member_biographies, null, (data) =>
      data
        ?.map(proposalsTools.activityMemberTools.addRightsToContributorBiography)
        ?.sort(proposalsTools.activityMemberTools.sortByUsedIn),
    ),
    getActivityContentDiff: httpTools.getOnceResponse<
      Omit<ResourceContentDifferenceOutputView<Content<ActivityContentData>>, "id">,
      ActivityContentDifferenceEntity
    >(
      builder,
      routes.proposals.api.activity_diff_content,
      null,
      (data) =>
        toResourceContentDifferenceEntity("activity-content-diff", data) as
          | ActivityContentDifferenceEntity
          | undefined,
    ),
    getActivityMemberBiographyDiff: httpTools.getOnceResponse<
      Omit<ResourceContentDifferenceOutputView<Content<ContributorBiographyContentData>>, "id">,
      MemberBiographyContentDifferenceEntity
    >(
      builder,
      routes.proposals.api.activity_member_diff_biography,
      null,
      (data) =>
        toResourceContentDifferenceEntity("member-biography-diff", data) as
          | MemberBiographyContentDifferenceEntity
          | undefined,
    ),
    notifyActivityMemberAboutPublication: httpTools.postOnceResponse<
      ActivityMemberOutputView,
      ActivityMemberEntity,
      Record<string, never>
    >(
      builder,
      routes.proposals.api.activity_member_notify_publication,
      "ActivityMember",
      (data) => (data ? proposalsTools.activityMemberTools.convertToDomainEntity(data) : undefined),
      "list",
      [
        { type: "ActivityMember", extractor: () => "LIST" },
        { type: "PublicationProposal", extractor: () => "LIST" },
      ],
    ),
    ensureProposalSpeakerRoom: builder.mutation<
      void,
      {
        params: { proposalId: Identifier; activityId?: Identifier };
        body: SpeakerRoomCreationInputView;
      }
    >({
      query: ({ params, body }) => ({
        url: routes.proposals.api.proposal_speaker_room_ensure(params),
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "ProgramProposal", id: arg.params.proposalId },
        ...(arg.params.activityId
          ? [{ type: "Activity" as const, id: arg.params.activityId }]
          : []),
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetSpeakerProposalQuery,
  useGetSpeakersProposalsQuery,
  useGetContributorProposalsQuery,
  useLazyGetContributorProposalsQuery,
  useGetActivityQuery,
  useBulkReadActivitiesMutation,
  usePatchActivityMutation,
  usePostActivityPresentationMutation,
  useGetActivityMembersQuery,
  useBulkReadActivityMembersMutation,
  useGetActivityMemberQuery,
  useGetActivityMemberNoCacheQuery,
  usePostActivityMemberMutation,
  usePostActivityMemberPhotoMutation,
  usePatchActivityMemberMutation,
  useDeleteActivityMemberMutation,
  useGetActivityMemberBiographiesNoCacheQuery,
  useGetProposalQuery,
  usePatchProposalMutation,
  useGetActivityContentDiffQuery,
  useGetActivityMemberBiographyDiffQuery,
  useNotifyActivityMemberAboutPublicationMutation,
  useEnsureProposalSpeakerRoomMutation,
} = proposalsApi;
